import { actions, assign, createMachine } from 'xstate';
import { recordMark } from '@stitch-fix/client-analytics-reporter';
import { fireEvent } from '@stitch-fix/kufak-gtm-client';
import {
  redirectUrlFromQueryParam,
  isPasswordlessLoginAuthError,
  type LoginResponseError,
} from '../../utils/requests/clientAuth/login';
import { browserLogger } from '../../utils/loggers/browser';
import type { SocialButtonErrorParams } from '../../components/SocialButtons/SocialButtons';
import { BannerType } from '../../components/SignIn/BannerWrapper';
import { getParams } from '../../utils/helpers';

type BannerContext = {
  message?: string | React.ReactNode;
  bannerType: BannerType;
};

type SignInContext = {
  banner?: BannerContext;
  recaptchaToken?: string;
  isDirectSignupClient: boolean;
  isWafEnabled: boolean;
};

type SignInEvent =
  | { type: 'GET_LINK_SUCCESS' }
  | { type: 'GET_LINK_FAILURE'; error: unknown }
  | { type: 'LOGIN_SUCCESS'; redirectUrl: string }
  | { type: 'PASSKEY_LOGIN_SUCCESS'; redirectUrl: string }
  | { type: 'LOGIN_FAILURE'; error: LoginResponseError; isWafEnabled: boolean }
  | {
      type: 'PASSKEY_LOGIN_FAILURE';
      error: LoginResponseError;
      isWafEnabled: boolean;
    }
  | {
      type: 'SSO_LOGIN_FAILURE';
      error: SocialButtonErrorParams;
      isWafEnabled: boolean;
    }
  | { type: 'CLOSE_BANNER' }
  | { type: 'SET_RECAPTCHA_TOKEN'; token: string }
  | { type: 'REDIRECT_TO_SIGNUP' }
  | { type: 'EMAIL_PASSWORD_SIGNIN' }
  | { type: 'PASSWORDLESS_SIGNIN' }
  | { type: 'DISPLAY_LOGIN_ERROR_BANNER'; error: BannerContext['bannerType'] };

export const createSignInMachine = (initial = 'emailPasswordSignIn') =>
  createMachine<SignInContext, SignInEvent>(
    {
      id: 'signIn',
      predictableActionArguments: true,
      initial,
      context: {
        banner: undefined,
        recaptchaToken: undefined,
        isDirectSignupClient: false,
        isWafEnabled: false,
      },
      on: {
        CLOSE_BANNER: { actions: 'closeBanner' },
      },
      states: {
        emailPasswordSignIn: {
          initial: 'idle',
          on: {
            LOGIN_SUCCESS: {
              target: 'redirect',
              actions: 'trackLoginAttempt',
            },
            PASSKEY_LOGIN_SUCCESS: {
              target: 'redirect',
              actions: 'trackLoginAttempt',
            },
            REDIRECT_TO_SIGNUP: {
              target: 'redirect',
            },
            SSO_LOGIN_FAILURE: {
              actions: 'logError',
            },
            LOGIN_FAILURE: [
              {
                cond: 'isRecaptchaError',
                target: '.recaptchaRequired',
              },
              {
                actions: [
                  'displayLoginErrorBanner',
                  'trackLoginAttempt',
                  'logError',
                ],
              },
            ],
            PASSKEY_LOGIN_FAILURE: [
              {
                cond: 'isRecaptchaError',
                target: '.recaptchaRequired',
              },
              {
                actions: [
                  'displayLoginErrorBanner',
                  'trackLoginAttempt',
                  'logError',
                ],
              },
            ],
            PASSWORDLESS_SIGNIN: {
              target: 'passwordlessSignIn',
            },
            DISPLAY_LOGIN_ERROR_BANNER: {
              actions: 'assignDataToContext',
            },
          },
          states: {
            idle: {},
            recaptchaRequired: {
              on: {
                SET_RECAPTCHA_TOKEN: {
                  actions: 'assignDataToContext',
                },
              },
            },
          },
        },
        passwordlessSignIn: {
          initial: 'idle',
          on: {
            GET_LINK_SUCCESS: {
              target: '.linkSentSuccess',
              actions: 'trackGetLinkSuccess',
            },
            GET_LINK_FAILURE: {
              actions: 'handleGetLinkFailure',
            },
            LOGIN_SUCCESS: {
              target: 'redirect',
              actions: 'trackLoginAttempt',
            },
            EMAIL_PASSWORD_SIGNIN: {
              target: 'emailPasswordSignIn',
            },
          },
          states: {
            idle: {
              entry: 'closeBanner',
            },
            linkSentSuccess: {
              entry: 'closeBanner',
              exit: 'closeBanner',
              on: {
                EMAIL_PASSWORD_SIGNIN: {
                  target: '#signIn.emailPasswordSignIn',
                },
                GET_LINK_SUCCESS: {
                  actions: [
                    'displayResendConfirmationBanner',
                    'trackGetLinkSuccess',
                  ],
                },
                GET_LINK_FAILURE: {
                  actions: 'handleGetLinkFailure',
                },
              },
            },
          },
        },
        redirect: {
          type: 'final',
          entry: 'redirect',
        },
      },
    },
    {
      guards: {
        isRecaptchaError: (_context, event) =>
          (event.type === 'LOGIN_FAILURE' ||
            event.type === 'PASSKEY_LOGIN_FAILURE') &&
          Boolean(event.error.recaptcha),
      },
      actions: {
        assignDataToContext: assign((context, event) => {
          if (event.type === 'SET_RECAPTCHA_TOKEN') {
            return {
              ...context,
              recaptchaToken: event.token,
            };
          }

          if (event.type === 'DISPLAY_LOGIN_ERROR_BANNER') {
            return {
              ...context,
              banner: {
                bannerType: event.error,
              },
            };
          }

          return context;
        }),
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        closeBanner: assign({ banner: _ => undefined }),
        displayLoginErrorBanner: assign({
          banner: (_, event) =>
            event.type === 'LOGIN_FAILURE' ||
            event.type === 'PASSKEY_LOGIN_FAILURE'
              ? { bannerType: 'error', message: event.error.error_message }
              : undefined,
        }),
        displayResendConfirmationBanner: assign({
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          banner: _ => ({
            bannerType: 'resend_confirmation',
          }),
        }),
        // using a `pure` action here instead of a guard because the checks for error type
        // and error code would need to be repeated in both the guard and action.
        // see docs: https://stately.ai/docs/xstate/actions/built-in-actions#pure-action
        handleGetLinkFailure: actions.pure((_context, event) => {
          if (event.type !== 'GET_LINK_FAILURE') {
            return [];
          }

          if (isPasswordlessLoginAuthError(event.error)) {
            const errorReasonMapping: Record<string, string> = {
              email_not_found: 'account_not_found',
              client_inactive: 'account_inactive',
            };

            const { code: errorCode } = event.error.response.data.error;

            // show error banner and track the failure for email_not_found and client_inactive errors
            if (
              errorCode === 'email_not_found' ||
              errorCode === 'client_inactive'
            ) {
              return [
                assign({
                  // eslint-disable-next-line @typescript-eslint/no-unused-vars
                  banner: _ => ({
                    bannerType: errorCode,
                  }),
                }),
                {
                  type: 'trackGetLinkFailure',
                  reason: errorReasonMapping[errorCode],
                },
              ];
            }
          }

          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          return [assign({ banner: _ => ({ bannerType: 'unknown_error' }) })];
        }),
        redirect: (context, event) => {
          if (
            event.type === 'LOGIN_SUCCESS' ||
            event.type === 'PASSKEY_LOGIN_SUCCESS'
          ) {
            const redirect = redirectUrlFromQueryParam() || event.redirectUrl;

            window.location.assign(redirect ?? '/home');
          }

          if (event.type === 'REDIRECT_TO_SIGNUP') {
            const params = getParams();
            const orderReviewParams = encodeURIComponent(
              params?.get('order_review_params') ?? '',
            );
            const businessLine = encodeURIComponent(
              params?.get('business_line') ?? '',
            );

            window.location.assign(
              // When the user came from a specialized signup form, return them there.
              context.isDirectSignupClient
                ? `/product/signup?business_line=${businessLine}&order_review_params=${orderReviewParams}`
                : '/signup',
            );
          }
        },
        trackGetLinkSuccess: () => {
          recordMark('passwordless_signin_request', { success: true });
        },
        trackGetLinkFailure: (_context, _event, { action }) => {
          recordMark('passwordless_signin_request', {
            success: false,
            reason: action.reason,
          });
        },
        trackLoginAttempt: (context, event) => {
          if (event.type === 'PASSKEY_LOGIN_SUCCESS') {
            browserLogger({
              level: 'info',
              team: 'client-identity-access-management',
              feature: 'passkeys',
              message:
                'attempting to fire sign-in-attempt event for passkey login',
            });
          }

          fireEvent({
            eventName: 'sign-in-attempt',
            eventAttributes: {
              signInType: 'email',
              signInSuccess:
                event.type === 'LOGIN_SUCCESS' ||
                event.type === 'PASSKEY_LOGIN_SUCCESS',
            },
          });

          if (event.type === 'PASSKEY_LOGIN_SUCCESS') {
            browserLogger({
              level: 'info',
              team: 'client-identity-access-management',
              feature: 'passkeys',
              message:
                'completed firing of sign-in-attempt event for passkey login',
            });
          }
        },
        logError: (_, event) => {
          if (
            event.type === 'SSO_LOGIN_FAILURE' ||
            event.type === 'LOGIN_FAILURE'
          ) {
            browserLogger({
              level: 'info',
              team: 'client-onboarding',
              feature: 'email-login',
              message: 'Sign In error',
              context: { error: event.error, isWafEnabled: event.isWafEnabled },
            });
          } else if (event.type === 'PASSKEY_LOGIN_FAILURE') {
            browserLogger({
              level: 'info',
              team: 'client-identity-access-management',
              feature: 'passkeys',
              message: 'Passkey Sign In error',
              context: { error: event.error, isWafEnabled: event.isWafEnabled },
            });
          }
        },
      },
    },
  );

export const signInMachine = createSignInMachine('emailPasswordSignIn');
